import React, { useState } from "react";
import { Search, FlaskConical, FileText, Info } from "lucide-react";
import { Input, Button, StatusBadge, toast, EmptyState } from "@/components/ds";
import { api, formatApiError, type RetrievalResult } from "@/lib/api";
import { catLabel } from "./constants";

const MODES = [
  { key: "full", label: "Full AI Context" },
  { key: "knowledge", label: "Knowledge" },
  { key: "services", label: "Services" },
  { key: "pricing", label: "Pricing" },
  { key: "service_area", label: "Service Area" },
  { key: "appointment_type", label: "Appointment Type" },
  { key: "availability", label: "Availability" },
];

export function RetrievalTest() {
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState("full");
  const [location, setLocation] = useState("");
  const [result, setResult] = useState<RetrievalResult | null>(null);
  const [ctxResult, setCtxResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (question.trim().length < 1) { toast.error("Enter a question to test."); return; }
    setLoading(true);
    try {
      if (mode === "knowledge") {
        setCtxResult(null);
        setResult(await api.kbRetrieve(question.trim()));
      } else {
        setResult(null);
        setCtxResult(await api.kbAiContextTest(question.trim(), mode, location.trim() || undefined));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="kb-retrieval-test">
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        <p className="text-sm text-slate-600">
          Preview the exact unified data your AI receptionist uses across the browser test call, simulated call, voice and SMS —
          knowledge, services, pricing disclosure, service areas, appointment types and live availability.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="kb-mode-row">
        {MODES.map((m) => (
          <button key={m.key} onClick={() => setMode(m.key)} data-testid={`kb-mode-${m.key}`}
            className={`rounded-full px-3 py-1 text-xs font-medium ${mode === m.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="e.g. How much is AC repair? · Do you service Dallas? · What are your hours?"
            className="pl-9" data-testid="kb-retrieval-input" />
        </div>
        {mode === "service_area" && (
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City or ZIP to check"
            className="w-44" data-testid="kb-location-input" />
        )}
        <Button onClick={run} disabled={loading} data-testid="kb-retrieval-run">
          <FlaskConical className="h-4 w-4" /> {loading ? "Testing…" : "Run test"}
        </Button>
      </div>

      {ctxResult && (
        <div className="space-y-3" data-testid="kb-context-result">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge tone="blue">source: {ctxResult.source_used}</StatusBadge>
            {ctxResult.availability_tool_required && <StatusBadge tone="amber">availability tool required</StatusBadge>}
            {ctxResult.disclosure_allowed !== null && ctxResult.disclosure_allowed !== undefined &&
              <StatusBadge tone={ctxResult.disclosure_allowed ? "green" : "slate"}>{ctxResult.disclosure_allowed ? "price disclosure allowed" : "pricing private"}</StatusBadge>}
          </div>
          {mode === "pricing" && (ctxResult.pricing || []).map((p: any, i: number) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`kb-pricing-${i}`}>
              <div className="flex items-center justify-between"><span className="font-medium text-slate-900">{p.name}</span>
                <StatusBadge tone={p.price_disclosable ? "green" : "slate"}>{p.price_disclosable ? "disclosable" : "private"}</StatusBadge></div>
              <p className="mt-1 text-slate-600">AI would say: <span className="italic">{p.ai_response}</span></p>
            </div>
          ))}
          {mode !== "pricing" && (
            <pre className="max-h-[420px] overflow-auto rounded-lg border border-slate-200 bg-slate-900 p-4 text-xs text-slate-100" data-testid="kb-context-json">
              {JSON.stringify(ctxResult.data ?? ctxResult, null, 2)}
            </pre>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="kb-retrieval-result">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge tone="blue" data-testid="kb-retrieval-count">{result.count} source(s) would be provided</StatusBadge>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{result.eligible_pool_size} eligible entr(y/ies) in your knowledge base</span>
          </div>
          {result.sources.length === 0 ? (
            <EmptyState icon={Search} title="No eligible sources"
              description="No approved, active, customer-usable entries matched. Add or approve knowledge entries to power your AI." />
          ) : (
            <ol className="space-y-3">
              {result.sources.map((s, i) => (
                <li key={s.entry_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`kb-source-${s.entry_id}`}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">{i + 1}</span>
                    <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                    <StatusBadge tone="slate">{catLabel(s.category)}</StatusBadge>
                  </div>
                  <p className="text-sm text-slate-600">{s.snippet}</p>
                  <p className="mt-2 font-mono text-[11px] text-slate-400" data-testid={`kb-source-ref-${s.entry_id}`}>
                    source_type: {s.source_type} · entry_id: {s.entry_id}
                  </p>
                </li>
              ))}
            </ol>
          )}
          <p className="text-center text-xs text-slate-400">{result.note}</p>
        </div>
      )}
    </div>
  );
}
